import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";
import { useAuthStore } from "@/store/auth";
import { useToastHandler } from "@/hooks/useToastHandler";
import { StatsOverviewResponse } from "@/types/statsTypes";

const ERROR_MSG = "Failed to fetch stats overview";
const API_PATH = "/api/stats/overview";
const USE_QUERY_KEY = API_PATH;

// Hook for fetching stats overview data
export function useStatsOverview(
  days_range: number,
  exercise_nano_id?: string | null
) {
  const { token } = useAuthStore();
  const { handleError } = useToastHandler();

  return useQuery<StatsOverviewResponse>({
    enabled: !!token,
    queryKey: [USE_QUERY_KEY, days_range, exercise_nano_id ?? null],
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      try {
        const res = await api.get(API_PATH, {
          params: { days_range, exercise_nano_id },
        });

        if (import.meta.env.DEV) {
          console.log(
            `>>> React Hook Fetching .... [${USE_QUERY_KEY}, ${days_range}, ${exercise_nano_id}]`,
            res.data
          );
        }

        // Check if the response is in the correct format
        if (
          res.data &&
          typeof res.data === "object" &&
          "overview" in res.data
        ) {
          return res.data as StatsOverviewResponse;
        }

        throw new Error(ERROR_MSG + " (Invalid Data)");
      } catch (err: unknown) {
        handleError(
          err instanceof Error ? err.message : ERROR_MSG,
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );
        throw err;
      }
    },
  });
}

// Invalidate cache
export function useInvalidateStatsOverview() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [USE_QUERY_KEY] });
  };
}

// Refetch cache
export function useRefetchStatsOverview() {
  const { refetch } = useStatsOverview(30); // Default to 30 days
  return refetch;
}
